import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Trophy, Calendar, TrendingUp } from 'lucide-react';
import { theme } from '../../theme';
import { API_BASE_URL, isGuestMode } from '../../config/api';

const MOCK_STATS = {
  current_streak: 7,
  longest_streak: 21,
  month_no_spend_days: 15,
  month_percentage_paid: 42.5,
};

export function ArenaStats() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [todayLogged, setTodayLogged] = useState(false);

  const guest = isGuestMode();

  useEffect(() => {
    if (guest) {
      setStats(MOCK_STATS);
      setLoading(false);
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/arena/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const logNoSpendDay = async () => {
    if (guest) {
      setTodayLogged(true);
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/arena/activities/no-spend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ date: today }),
      });
      if (response.ok) {
        setTodayLogged(true);
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to log no-spend day:', err);
    }
  };

  if (loading) return <div>Loading stats...</div>;
  if (!stats) return <div>Failed to load stats</div>;

  return (
    <div>
      {/* Daily Check-in */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: theme.semantic.bg.elevated,
          border: `2px solid ${theme.semantic.border.strong}`,
          borderRadius: theme.radii.md,
          padding: theme.spacing[6],
          marginBottom: theme.spacing[6],
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: theme.spacing[3],
        }}>
          <div>
            <h3 style={{ margin: 0 }}>Daily Check-in</h3>
            <p style={{ color: theme.semantic.text.secondary }}>
              {todayLogged ? "Today's no-spend day is logged!" : "Log your no-spend day"}
            </p>
          </div>
          {!todayLogged && (
            <button
              onClick={logNoSpendDay}
              style={{
                background: theme.semantic.action.primary,
                color: theme.semantic.text.primary,
                border: `2px solid ${theme.semantic.border.strong}`,
                borderRadius: theme.radii.md,
                padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
                cursor: 'pointer',
                fontWeight: theme.typography.fontWeight.bold,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.semantic.action.primaryHover;
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme.semantic.action.primary;
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Log No-Spend Day
            </button>
          )}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
        gap: theme.spacing[4],
        overflow: 'hidden',
      }}>
        <div style={{ background: theme.semantic.bg.surface, borderRadius: theme.radii.md, padding: theme.spacing[5] }}>
          <Flame size={24} color={theme.brand.crimson} />
          <p style={{ color: theme.semantic.text.secondary }}>Current Streak</p>
          <div style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.black }}>
            {stats.current_streak} days
          </div>
        </div>

        <div style={{ background: theme.semantic.bg.surface, borderRadius: theme.radii.md, padding: theme.spacing[5] }}>
          <Trophy size={24} color={theme.brand.copper} />
          <p style={{ color: theme.semantic.text.secondary }}>Longest Streak</p>
          <div style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.black }}>
            {stats.longest_streak} days
          </div>
        </div>

        <div style={{ background: theme.semantic.bg.surface, borderRadius: theme.radii.md, padding: theme.spacing[5] }}>
          <Calendar size={24} color={theme.colors.success} />
          <p style={{ color: theme.semantic.text.secondary }}>Month No-Spend</p>
          <div style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.black }}>
            {stats.month_no_spend_days} days
          </div>
        </div>

        <div style={{ background: theme.semantic.bg.surface, borderRadius: theme.radii.md, padding: theme.spacing[5] }}>
          <TrendingUp size={24} color={theme.brand.copper} />
          <p style={{ color: theme.semantic.text.secondary }}>Debt Crushed</p>
          <div style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.black }}>
            {stats.month_percentage_paid?.toFixed(1) || 0}%
          </div>
        </div>
      </div>
    </div>
  );
}